/**
 * Modal focus-trap — keep Tab focus inside an open modal, restore it on close.
 *
 * This is the cross-cutting concern that was missing from EVERY modal in the IST
 * (current-state.md §2 "Does NOT hold" — no focus-trap, no restore-to-trigger).
 * It is now a free opt-in via `data-datte-focus-trap` (default on).
 *
 * Behaviour:
 *   - On Tab / Shift+Tab while open, focus wraps within the modal's focusable
 *     elements instead of escaping to the page behind the backdrop.
 *   - The element that had focus before the modal opened (the trigger) is
 *     captured on `shown` and re-focused on `hidden`, so keyboard users land back
 *     where they were.
 *
 * The open/close lifecycle is driven by the controller through the engine's
 * `onShown` / `onHidden` hooks; this module only installs the keydown trap and
 * exposes capture/restore. Returns an `Unbind` for full teardown.
 *
 * @see docs/architecture/modals/target-architecture.md §3.2.2
 */

import type { Unbind } from '../utils/unbind';
import { modalEngine } from './modal-engine';

/**
 * Selectors for natively focusable / explicitly-focusable elements. `[disabled]`
 * and negative tabindex are filtered out afterwards (a CSS selector can't express
 * "tabindex that isn't -1" portably).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
  '[tabindex]',
].join(',');

/**
 * The focusable descendants of `root`, in DOM order, excluding disabled,
 * `tabindex="-1"`, and visually-hidden (display:none / no layout box) elements.
 *
 * Pure-ish helper (reads layout via `offsetParent`) — unit-testable in happy-dom.
 */
export function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // offsetParent is null for display:none elements (and fixed-position ones, but
    // modals are not fixed children here); a 0-box element is also unfocusable.
    return el.offsetParent !== null || el.getClientRects().length > 0;
  });
}

/** True for the Tab key, regardless of modifiers. */
function isTabKey(e: KeyboardEvent): boolean {
  return e.key === 'Tab' || e.keyCode === 9;
}

export function setupModalFocusTrap(modal: HTMLElement): Unbind {
  let previouslyFocused: HTMLElement | null = null;

  const onKeydown = (e: KeyboardEvent) => {
    if (!isTabKey(e)) return;

    const focusable = focusableElements(modal);
    if (focusable.length === 0) {
      // Nothing to focus inside — keep focus on the modal root so Tab can't escape.
      e.preventDefault();
      modal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !modal.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !modal.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  };

  const onShown = () => {
    // Capture whatever had focus before the modal grabbed it (the trigger).
    previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modal.addEventListener('keydown', onKeydown, true);

    // Move focus into the modal so the very first Tab is already trapped.
    const focusable = focusableElements(modal);
    (focusable[0] ?? modal).focus();
  };

  const onHidden = () => {
    modal.removeEventListener('keydown', onKeydown, true);
    // Restore focus to the trigger; guard against a detached / hidden element.
    if (previouslyFocused && previouslyFocused.isConnected) {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  };

  const unbindShown = modalEngine.onShown(modal, onShown);
  const unbindHidden = modalEngine.onHidden(modal, onHidden);

  return () => {
    unbindShown();
    unbindHidden();
    modal.removeEventListener('keydown', onKeydown, true);
    previouslyFocused = null;
  };
}
